import {
    BoundarySign,
    ExactFilter,
    ExactSign,
    Filter,
    FilterValueType,
    isExactFilter,
    isMembershipFilter,
    isRangeFilter,
    MembershipFilter,
    MembershipSign,
    RangeFilter,
    signDoesNotNeedValue,
    TodayFilter,
} from 'utils/filter/filter';
import { isNumber, toNumber } from 'utils/validations/number';
import { fromTime, isTime, toTime } from 'utils/validations/time';
import { toDate } from 'utils/validations/date';
import {
    applyPeriod,
    ValueType,
} from '@instinet-analytics/semantic-ui-components';
import {
    isPeriod,
    PeriodValue,
} from '@instinet-analytics/semantic-ui-components';

export const TodayValue = 'Today';

export type SupportedComparisonValue =
    | string
    | string[]
    | number
    | undefined
    | PeriodValue;

export interface ExactComparison {
    valueType: FilterValueType;
    sign: ExactSign;
    value?: SupportedComparisonValue;
}

export interface BoundaryComparison {
    valueType: FilterValueType;
    sign: BoundarySign | undefined;
    value?: SupportedComparisonValue;
}

export interface LowerBoundaryComparison extends BoundaryComparison {
    sign: BoundarySign.GT | BoundarySign.GTE;
}

export interface UpperBoundaryComparison extends BoundaryComparison {
    sign: BoundarySign.LT | BoundarySign.LTE;
}

export type RangeComparison = [BoundaryComparison, BoundaryComparison];

export interface MembershipComparison {
    valueType: FilterValueType;
    sign: MembershipSign;
    values: SupportedComparisonValue[];
}

export type Comparison =
    | ExactComparison
    | RangeComparison
    | MembershipComparison;

export const isExactComparison = (
    comparison: Comparison
): comparison is ExactComparison =>
    (comparison as ExactComparison).sign === ExactSign.EQ ||
    (comparison as ExactComparison).sign === ExactSign.NEQ;

export const isMembershipComparison = (
    comparison: Comparison
): comparison is MembershipComparison =>
    (comparison as MembershipComparison).sign === MembershipSign.INCLUDE ||
    (comparison as MembershipComparison).sign === MembershipSign.EXCLUDE ||
    (comparison as MembershipComparison).sign === MembershipSign.CONTAINS ||
    (comparison as MembershipComparison).sign === MembershipSign.NOTCONTAINS ||
    (comparison as MembershipComparison).sign === MembershipSign.STARTSWITH ||
    (comparison as MembershipComparison).sign === MembershipSign.ENDSWITH ||
    (comparison as MembershipComparison).sign === MembershipSign.BLANK ||
    (comparison as MembershipComparison).sign === MembershipSign.NOTBLANK;

export const isRangeComparison = (
    comparison: Comparison
): comparison is RangeComparison =>
    !!(comparison as RangeComparison)[0] &&
    !!(comparison as RangeComparison)[1];

const fromExactFilter = <T>(exactFilter: ExactFilter<T>): ExactComparison => {
    return {
        sign: exactFilter.exactSign,
        valueType: exactFilter.valueType,
        value: fromFilterValue(exactFilter.valueType, exactFilter.value),
    };
};

const fromMembershipFilter = <T>(
    membershipFilter: MembershipFilter<T>
): MembershipComparison => {
    return {
        valueType: membershipFilter.valueType,
        sign: membershipFilter.membershipSign,
        values: membershipFilter.values.map(
            (value) => fromFilterValue(membershipFilter.valueType, value)!
        ),
    };
};

const fromRangeFilter = <T>(rangeFilter: RangeFilter<T>): RangeComparison => {
    const rangeComparison = [
        {
            valueType: rangeFilter.valueType,
            sign: rangeFilter.lowerSign,
            value: fromFilterValue(
                rangeFilter.valueType,
                rangeFilter.lowerValue
            ),
        },
        {
            valueType: rangeFilter.valueType,
            sign: rangeFilter.upperSign,
            value: fromFilterValue(
                rangeFilter.valueType,
                rangeFilter.upperValue
            ),
        },
    ];

    // if its a single-sided range comparison, the second comparison should be the one that is undefined
    return rangeComparison.sort((a, b) => {
        if (a.sign === undefined && b.sign === undefined) {
            return 0;
        }

        return a.sign === undefined ? 1 : -1;
    }) as RangeComparison;
};

export const fromFilter = <T>(filter: Filter<T>): Comparison => {
    if (isExactFilter(filter)) {
        return fromExactFilter(filter);
    } else if (isMembershipFilter(filter)) {
        return fromMembershipFilter(filter);
    } else if (isRangeFilter(filter)) {
        return fromRangeFilter(filter);
    }
    throw new Error('Cannot create comparison from unknown filter');
};

const toExactFilter = <T>(comparison: ExactComparison): ExactFilter<T> => {
    return {
        valueType: comparison.valueType,
        exactSign: comparison.sign,
        value: toFilterValue(
            comparison.valueType,
            comparison.value
        ) as unknown as T,
    };
};

const toDateFilter = <T>(
    comparison: ExactComparison
): ExactFilter<T> | RangeFilter<T> | TodayFilter => {
    if (Array.isArray(comparison.value)) {
        return {
            valueType: comparison.valueType,
            lowerSign: BoundarySign.GTE,
            lowerValue: toFilterValue(
                comparison.valueType,
                comparison.value![0]
            ) as unknown as T,
            upperSign: BoundarySign.LTE,
            upperValue: toFilterValue(
                comparison.valueType,
                comparison.value![1]
            ) as unknown as T,
        };
    }

    return {
        valueType: comparison.valueType,
        exactSign: comparison.sign,
        value: toFilterValue(
            comparison.valueType,
            comparison.value
        ) as unknown as T,
    };
};

const toMembershipFilter = <T>(
    membershipComparison: MembershipComparison
): MembershipFilter<T> => {
    return {
        valueType: membershipComparison.valueType,
        membershipSign: membershipComparison.sign,
        values: membershipComparison.values.map(
            (value) =>
                toFilterValue(
                    membershipComparison.valueType,
                    value
                ) as unknown as T
        ),
    };
};

const toRangeFilter = <T>(rangeComparison: RangeComparison): RangeFilter<T> => {
    const [lowerComparison, upperComparison] =
        sortRangeComparison(rangeComparison);

    return {
        valueType: lowerComparison.valueType,
        lowerSign: lowerComparison.value ? lowerComparison.sign : undefined,
        lowerValue: toFilterValue(
            lowerComparison.valueType,
            lowerComparison.value
        ) as unknown as T,
        upperSign: upperComparison.value ? upperComparison.sign : undefined,
        upperValue: toFilterValue(
            upperComparison.valueType,
            upperComparison.value
        ) as unknown as T,
    };
};

export const toFilter = <T>(comparison: Comparison): Filter<T> => {
    if (isExactComparison(comparison)) {
        if (
            comparison.valueType === ValueType.DATE ||
            comparison.valueType === ValueType.DATETIME
        ) {
            return toDateFilter(comparison);
        }
        return toExactFilter(comparison as ExactComparison);
    } else if (isMembershipComparison(comparison)) {
        return toMembershipFilter(comparison as MembershipComparison);
    } else if (isRangeComparison(comparison)) {
        return toRangeFilter(comparison as RangeComparison);
    }
    throw new Error('Cannot create filter from unknown comparison');
};

function sortRangeComparison(rangeComparison: RangeComparison) {
    let lowerComparison;
    let upperComparison;

    if (
        rangeComparison[0].sign === BoundarySign.GT ||
        rangeComparison[0].sign === BoundarySign.GTE
    ) {
        lowerComparison = rangeComparison[0];
        upperComparison = rangeComparison[1];
    } else {
        lowerComparison = rangeComparison[1];
        upperComparison = rangeComparison[0];
    }

    return [lowerComparison, upperComparison] as [
        LowerBoundaryComparison,
        UpperBoundaryComparison,
    ];
}

function fromFilterValue<T>(valueType: FilterValueType, value: T) {
    if (value === undefined) {
        return '';
    }

    switch (valueType) {
        case ValueType.ENUM:
        case ValueType.DATE:
        case ValueType.DATETIME:
        case ValueType.STRING:
            return value as unknown as string;
        case ValueType.NUMBER:
            return (value as unknown as number) + '';
        case ValueType.TIME:
            return fromTime(value as unknown as number);
        default:
            return undefined;
    }
}

function toFilterValue(
    valueType: FilterValueType,
    value: SupportedComparisonValue
) {
    if (!value) {
        return undefined;
    }

    switch (valueType) {
        case ValueType.STRING:
            return value;
        case ValueType.NUMBER:
            return toNumber(value as string);
        case ValueType.TIME:
            return toTime(value as string[]);
        case ValueType.DATE:
        case ValueType.DATETIME:
            return value === TodayValue
                ? TodayValue
                : isPeriod(value)
                ? value
                : toDate(value as string);
        case ValueType.ENUM:
            return value;
        default:
            return undefined;
    }
}

export const isRangeComparisonValid = (rangeComparison: RangeComparison) => {
    const [lowerComparison, upperComparison] =
        sortRangeComparison(rangeComparison);

    if (
        (lowerComparison.value && !upperComparison.value) ||
        (!lowerComparison.value && upperComparison.value)
    ) {
        return true;
    }

    if (!lowerComparison.sign || !upperComparison.sign) {
        return false;
    }

    if (
        lowerComparison.sign === BoundarySign.GT ||
        upperComparison.sign === BoundarySign.LT
    ) {
        if (lowerComparison.valueType === ValueType.TIME) {
            return (
                toTime(lowerComparison.value as string[]) <
                toTime(upperComparison.value as string[])
            );
        } else if (
            lowerComparison.valueType === ValueType.DATE ||
            lowerComparison.valueType === ValueType.DATETIME
        ) {
            const lowerValue = getDateValue(lowerComparison.value);
            const upperValue = getDateValue(upperComparison.value);
            return lowerValue < upperValue;
        } else {
            return (
                toNumber(lowerComparison.value as string) <
                toNumber(upperComparison.value as string)
            );
        }
    }

    if (
        lowerComparison.sign === BoundarySign.GTE &&
        upperComparison.sign === BoundarySign.LTE
    ) {
        if (lowerComparison.valueType === ValueType.TIME) {
            return (
                toTime(lowerComparison.value as string[]) <=
                toTime(upperComparison.value as string[])
            );
        } else if (lowerComparison.valueType === ValueType.DATE) {
            return (
                (lowerComparison.value as number) <=
                (upperComparison.value as number)
            );
        } else {
            return (
                toNumber(lowerComparison.value as string) <=
                toNumber(upperComparison.value as string)
            );
        }
    }

    return false;
};

export const isComparisonValueValid = (
    valueType: FilterValueType,
    value: SupportedComparisonValue
) => {
    if (!value) {
        return true;
    }
    switch (valueType) {
        case ValueType.STRING:
        case ValueType.ENUM:
        case ValueType.DATETIME:
        case ValueType.DATE:
            return true;
        case ValueType.TIME:
            return value === 'Today' || isTime(value as string[]);
        case ValueType.NUMBER:
            return isNumber(value as string);
    }
};

export const isComparisonValid = (comparison: Comparison) => {
    if (isExactComparison(comparison)) {
        return (
            isComparisonValueValid(comparison.valueType, comparison.value) &&
            !!comparison.value
        );
    } else if (isMembershipComparison(comparison)) {
        return (
            signDoesNotNeedValue(comparison.sign) ||
            (!!comparison.values.length &&
                comparison.values.every((x) =>
                    isComparisonValueValid(comparison.valueType, x)
                ))
        );
    } else if (isRangeComparison(comparison)) {
        if (!isRangeComparisonValid(comparison)) {
            return false;
        }

        const [firstComparison, secondComparison] = comparison;
        return !(
            !isComparisonValueValid(
                firstComparison.valueType,
                firstComparison.value
            ) ||
            !isComparisonValueValid(
                secondComparison.valueType,
                secondComparison.value
            )
        );
    }
};

function getDateValue(value: number | SupportedComparisonValue) {
    if (isPeriod(value)) {
        return applyPeriod(value, new Date()).toMillis();
    }
    return value as number;
}
